import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  input,
  output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

/**
 * Primary filled CTA with a built-in, layout-stable loading state.
 *
 * The whole point is consistent, *non-jumping* feedback: when `loading`
 * flips on, the projected label keeps its footprint (rendered at
 * `opacity: 0`) and a spinner is overlaid dead-centre. The button never
 * changes width and the label never shifts sideways — the exact
 * "renders weirdly" problem the old inline `mat-progress-spinner` (placed
 * *next to* the label with a flex `gap`) caused on the email-slug step.
 *
 * Usage:
 *   <app-spinner-button
 *     [loading]="busy()"
 *     [disabled]="form.invalid"
 *     type="submit">
 *     {{ 'onboarding.shell.continue' | translate }}
 *     <mat-icon>arrow_forward</mat-icon>
 *   </app-spinner-button>
 *
 * For buttons inside a `<form>`, pass `type="submit"` and let the form's
 * `(ngSubmit)` handle the action; otherwise bind `(clicked)`.
 */
@Component({
  selector: 'app-spinner-button',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatProgressSpinnerModule],
  template: `
    <button
      mat-flat-button
      [color]="color()"
      [type]="type()"
      [disabled]="disabled() || loading()"
      [attr.aria-busy]="loading()"
      class="sb-button"
      (click)="onClick($event)"
    >
      <span class="sb-label" [class.sb-label--loading]="loading()">
        <ng-content></ng-content>
      </span>
      @if (loading()) {
        <mat-progress-spinner
          class="sb-spinner"
          [diameter]="spinnerDiameter()"
          mode="indeterminate"
        ></mat-progress-spinner>
      }
    </button>
  `,
  styles: [`
    :host {
      display: inline-flex;
    }

    .sb-button {
      position: relative;
    }

    .sb-label {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: opacity 0.12s ease;
    }

    /* Keep the label's footprint so the button never resizes, but hide it
       behind the centred spinner. */
    .sb-label--loading {
      opacity: 0;
    }

    .sb-spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    /* Make the indeterminate spinner inherit the button's foreground colour
       so it stays visible on a filled (primary) background. */
    ::ng-deep .sb-spinner circle {
      stroke: currentColor;
    }
  `],
})
export class SpinnerButtonComponent {
  /** When true, disables the button and overlays a centred spinner. */
  readonly loading = input(false, { transform: booleanAttribute });

  /** Independent disabled state (combined with `loading`). */
  readonly disabled = input(false, { transform: booleanAttribute });

  /** Material theme palette — defaults to the primary CTA colour. */
  readonly color = input<'primary' | 'accent' | 'warn' | undefined>('primary');

  /** Native button type. Use `submit` inside a form, `button` otherwise. */
  readonly type = input<'button' | 'submit'>('button');

  /** Spinner size — 20px reads well inside a standard-height button. */
  readonly spinnerDiameter = input<number>(20);

  /** Emitted on click. Suppressed while loading/disabled (button is inert). */
  readonly clicked = output<MouseEvent>();

  onClick(event: MouseEvent): void {
    if (this.loading() || this.disabled()) return;
    this.clicked.emit(event);
  }
}
